import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { focusRing } from "theme/colors";
import CustomDropzone from "global/components/CustomDropzone/CustomDropzone";
import DropzoneBody, {
  dropzoneSurfaceSx,
} from "global/components/CustomDropzone/DropzoneBody";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import {
  deleteSigner,
  setReduxSignRequest,
  updateIndividualSigner,
} from "redux/appSlice";
import { RequestParticipant } from "models/interfaces";
import { exportCSVFile } from "screens/Contacts/ContactFunction";
import { addSigner } from "../SignRequestHelper";
import bulkSendSignersTokens from "../BulkSendSigners.tokens";
import { tourAttr } from "tour";
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  DownloadIcon,
  FileIcon,
  FileLinesIcon,
  InfoIcon,
  PlusIcon,
  SparkleIcon,
  SparkleSmallIcon,
  TrashIcon,
  TrashSimpleIcon,
} from "./BulkSendSignersIcons";

const t = bulkSendSignersTokens;
const p = t.palette;

const emptySigner = { id: 0, name: "", email: "", order: 0, color: "", cc: false };

type Filter = "all" | "errors" | "valid";
type View = "list" | "csvUpload" | "csvMapping";
type MapField = "name" | "email";

const isEmail = (v?: string) =>
  /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test((v || "").trim());

const emailError = (v?: string): string | null => {
  const e = (v || "").trim();
  if (!e) return "Email is required";
  if (/\s/.test(e)) return "Email cannot contain spaces";
  if (!e.includes("@")) return "Missing @ symbol";
  if (!/@[^\s@]+\./.test(e)) return "Missing domain (e.g. name@company.com)";
  if (!isEmail(e)) return "This doesn’t look like a valid email address";
  return null;
};

/** Minimal RFC-4180-ish CSV reader — quotes, escaped quotes and CRLF. */
const parseCsv = (text: string): Record<string, string>[] => {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else if (c !== "\r") field += c;
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }
  const nonEmpty = rows.filter((r) => r.some((cell) => cell.trim() !== ""));
  if (!nonEmpty.length) return [];
  const headers = nonEmpty[0].map((h) => h.trim());
  return nonEmpty.slice(1).map((r) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, idx) => {
      obj[h] = (r[idx] ?? "").trim();
    });
    return obj;
  });
};

const BulkSendParticipants = () => {
  const dispatch = useAppDispatch();
  const { signRequest } = useAppSelector((state: RootState) => state.app);
  const recipients: RequestParticipant[] = useMemo(
    () => signRequest.signers ?? [],
    [signRequest.signers],
  );

  const [filter, setFilter] = useState<Filter>("all");
  const [view, setView] = useState<View>("list");
  const [touched, setTouched] = useState<Record<string | number, boolean>>({});
  const [justImported, setJustImported] = useState<string | null>(null);

  // CSV import state
  const [csvFileName, setCsvFileName] = useState("");
  const [csvRaw, setCsvRaw] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<{ name: string | null; email: string | null }>({
    name: null,
    email: null,
  });
  const [aiMapped, setAiMapped] = useState<{ name: boolean; email: boolean }>({
    name: false,
    email: false,
  });
  const [openMenu, setOpenMenu] = useState<MapField | null>(null);

  // Close an open mapping dropdown when the user clicks outside of it.
  useEffect(() => {
    if (!openMenu) return;
    const onDoc = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest("[data-map-root]")) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [openMenu]);

  // ---- derived validity -----------------------------------------------------
  const decorated = recipients.map((r, i) => {
    const hasContent = !!(r.name || "").trim() || !!(r.email || "").trim();
    const isTouched = !!touched[r.id];
    const err = isTouched && hasContent ? emailError(r.email) : null;
    return {
      signer: r,
      num: i + 1,
      err,
      showError: !!err,
      invalid: !!err,
      showCheck: isTouched && !err && isEmail(r.email),
      fromCsv: !!(r as any).source,
      source: (r as any).source as string | undefined,
    };
  });
  const errorCount = decorated.filter((d) => d.invalid).length;
  const validCount = decorated.filter((d) => d.showCheck).length;
  const totalCount = recipients.length;
  const hasErrors = errorCount > 0;

  let rows = [...decorated].sort(
    (a, b) => (b.invalid ? 1 : 0) - (a.invalid ? 1 : 0),
  );
  if (filter === "errors") rows = decorated.filter((d) => d.invalid);
  if (filter === "valid") rows = decorated.filter((d) => d.showCheck);

  const filteredEmpty = totalCount > 0 && rows.length === 0;

  // ---- list handlers --------------------------------------------------------
  const setSignerField = (id: number, field: MapField, value: string) => {
    const fieldsToUpdate: Partial<RequestParticipant> =
      field === "name"
        ? { name: value }
        : { email: value, isValid: isEmail(value) };
    dispatch(updateIndividualSigner({ signerId: id, fieldsToUpdate }));
  };

  const markTouched = (id: number) =>
    setTouched((prev) => (prev[id] ? prev : { ...prev, [id]: true }));

  const addManual = () => {
    setFilter("all");
    setJustImported(null);
    addSigner(emptySigner, recipients, dispatch, true);
  };

  const removeRow = (id: number) => dispatch(deleteSigner({ signerId: id }));

  const removeAllErrors = () => {
    const kept = recipients.filter((r) => {
      const hasContent = !!(r.name || "").trim() || !!(r.email || "").trim();
      return !(touched[r.id] && hasContent && emailError(r.email));
    });
    dispatch(setReduxSignRequest({ signers: kept }));
    setFilter("all");
  };

  const downloadSample = () =>
    exportCSVFile(
      ["NAME", "EMAIL"],
      [
        ["John Doe", "john.doe@example.com"],
        ["Jane Smith", "jane.smith@example.com"],
      ],
      "bulk-send-example",
    );

  // ---- csv handlers ---------------------------------------------------------
  const startCsv = () => {
    setView("csvUpload");
    setJustImported(null);
    setOpenMenu(null);
  };
  const backToList = () => {
    setView("list");
    setOpenMenu(null);
  };
  const cancelCsv = () => {
    setView("list");
    setCsvRaw([]);
    setCsvFileName("");
    setMapping({ name: null, email: null });
    setOpenMenu(null);
  };

  // Takes the file itself rather than the input event: the surface is now a
  // `CustomDropzone`, which hands back `File[]` from either a drop or a browse.
  const handleCsvFile = (file?: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result || ""));
      const cols = parsed.length ? Object.keys(parsed[0]) : [];
      const nameCol = cols.find((c) => /name/i.test(c)) ?? null;
      const emailCol = cols.find((c) => /e-?mail/i.test(c)) ?? null;
      setCsvFileName(file.name);
      setCsvRaw(parsed);
      setMapping({ name: nameCol, email: emailCol });
      setAiMapped({
        name: !!nameCol && nameCol.toLowerCase() !== "name",
        email: !!emailCol && emailCol.toLowerCase() !== "email",
      });
      setView("csvMapping");
    };
    reader.readAsText(file);
  };

  const cols = csvRaw.length ? Object.keys(csvRaw[0]) : [];
  const mapReady =
    !!mapping.name && !!mapping.email && mapping.name !== mapping.email;

  const mapPick = (field: MapField, col: string) => {
    setMapping((prev) => ({ ...prev, [field]: col }));
    setAiMapped((prev) => ({ ...prev, [field]: false }));
    setOpenMenu(null);
  };

  const confirmMapping = () => {
    if (!mapReady) return;
    const maxId = recipients.reduce((m, r) => Math.max(m, Number(r.id)), -1);
    const added = csvRaw.map((r, i) => ({
      id: maxId + 1 + i,
      name: r[mapping.name as string] || "",
      email: r[mapping.email as string] || "",
      order: 1,
      color: "#A2C579",
      cc: false,
      isValid: isEmail(r[mapping.email as string]),
      source: csvFileName,
    })) as unknown as RequestParticipant[];
    const errs = added.filter((r) => emailError(r.email)).length;
    dispatch(setReduxSignRequest({ signers: [...recipients, ...added] }));
    setTouched((prev) => {
      const next = { ...prev };
      added.forEach((r) => {
        next[r.id] = true;
      });
      return next;
    });
    setView("list");
    setFilter(errs > 0 ? "errors" : "all");
    setJustImported(
      `${added.length} recipients added from ${csvFileName}` +
        (errs > 0 ? ` — ${errs} need a fixed email` : " — all valid"),
    );
    setCsvRaw([]);
    setCsvFileName("");
    setMapping({ name: null, email: null });
    setOpenMenu(null);
  };

  // ---- shared style fragments ----------------------------------------------
  const outlinedBtn = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: t.btn.outlinedBorder,
    backgroundColor: p.white,
    color: p.accent,
    fontFamily: "inherit",
    fontWeight: t.btn.fontWeight,
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: t.btn.outlinedHoverBg },
    "&:focus-visible": {
      outline: `2px solid ${focusRing}`,
      outlineOffset: "2px",
    },
  } as const;

  const solidBtn = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    backgroundColor: p.ctaBg,
    color: p.white,
    fontFamily: "inherit",
    fontWeight: t.btn.fontWeight,
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: t.btn.solidHoverBg },
    "&:focus-visible": {
      outline: `2px solid ${focusRing}`,
      outlineOffset: "2px",
    },
  } as const;

  const markPointerFocus = (e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.dataset.pointerFocus = "true";
  };
  const clearPointerFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    delete e.currentTarget.dataset.pointerFocus;
  };

  const inputBase = {
    flex: 1,
    minWidth: 0,
    height: t.rows.inputHeight,
    border: "none",
    borderRadius: 0,
    backgroundColor: "transparent",
    fontFamily: "inherit",
    fontSize: t.rows.inputFontSize,
    color: t.rows.inputColor,
    padding: `0 ${t.rows.inputPaddingX}`,
    boxSizing: "border-box",
    outline: "none",
    "&::placeholder": { color: t.rows.inputPlaceholder },
    "&:focus-visible": { outline: `2px solid ${focusRing}`, outlineOffset: "-2px" },
    '&[data-pointer-focus="true"]:focus-visible': { outline: "none" },
  } as const;

  // ---- filter pill ----------------------------------------------------------
  const filterPill = (
    key: Filter,
    label: string,
    count: number,
    icon: React.ReactNode | null,
    active: boolean,
    bg: string,
    border: string,
    color: string,
    badgeBg: string,
  ) => (
    <Box
      component="button"
      type="button"
      onClick={() => setFilter(key)}
      aria-pressed={active}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: t.filter.gap,
        height: t.filter.height,
        px: t.filter.paddingX,
        borderRadius: t.filter.radius,
        backgroundColor: bg,
        border: `1px solid ${border}`,
        color,
        fontFamily: "inherit",
        fontSize: t.filter.fontSize,
        fontWeight: t.filter.fontWeight,
        cursor: "pointer",
        userSelect: "none",
        "&:focus-visible": { outline: `2px solid ${focusRing}`, outlineOffset: "2px" },
      }}
    >
      {icon}
      <Box component="span" sx={{ whiteSpace: "nowrap" }}>
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          minWidth: t.filter.badgeMinWidth,
          height: t.filter.badgeHeight,
          borderRadius: t.filter.badgeRadius,
          backgroundColor: badgeBg,
          fontSize: t.filter.badgeFontSize,
          fontWeight: t.filter.badgeFontWeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: t.filter.badgePaddingX,
          boxSizing: "border-box",
        }}
      >
        {count}
      </Box>
    </Box>
  );

  const renderList = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: t.rows.rowGap }}>
      {/* sticky summary / actions bar */}
      <Box
        {...tourAttr("bulk.signersBar")}
        sx={{
          position: "sticky",
          top: t.bar.stickyTop,
          zIndex: 20,
          display: "flex",
          alignItems: "center",
          gap: t.bar.gap,
          p: t.bar.padding,
          borderRadius: t.bar.radius,
          backgroundColor: hasErrors ? t.bar.errBg : t.bar.okBg,
          border: `1px solid ${hasErrors ? t.bar.errBorder : t.bar.okBorder}`,
          boxShadow: t.bar.shadow,
        }}
      >
        {filterPill(
          "all",
          "All",
          totalCount,
          null,
          filter === "all",
          filter === "all" ? t.filter.allActiveBg : t.filter.allIdleBg,
          filter === "all" ? t.filter.allActiveBorder : t.filter.allIdleBorder,
          filter === "all" ? t.filter.allActiveColor : t.filter.allIdleColor,
          t.filter.allBadgeBg,
        )}
        {filterPill(
          "errors",
          "Needs fixing",
          errorCount,
          <AlertCircleIcon size={t.filter.iconSize} />,
          filter === "errors",
          filter === "errors" ? t.filter.errActiveBg : t.filter.errIdleBg,
          filter === "errors" ? t.filter.errActiveBorder : t.filter.errIdleBorder,
          filter === "errors" ? t.filter.errActiveColor : t.filter.errIdleColor,
          t.filter.errBadgeBg,
        )}
        {filterPill(
          "valid",
          "Valid",
          validCount,
          <CheckIcon size={t.filter.iconSize} />,
          filter === "valid",
          filter === "valid" ? t.filter.okActiveBg : t.filter.okIdleBg,
          filter === "valid" ? t.filter.okActiveBorder : t.filter.okIdleBorder,
          filter === "valid" ? t.filter.okActiveColor : t.filter.okIdleColor,
          t.filter.okBadgeBg,
        )}

        {hasErrors && (
          <Box
            component="button"
            type="button"
            onClick={removeAllErrors}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: t.removeAll.gap,
              height: t.removeAll.height,
              px: t.removeAll.paddingX,
              borderRadius: t.removeAll.radius,
              border: t.removeAll.border,
              backgroundColor: p.white,
              color: t.removeAll.color,
              fontFamily: "inherit",
              fontSize: t.removeAll.fontSize,
              fontWeight: t.removeAll.fontWeight,
              cursor: "pointer",
              whiteSpace: "nowrap",
              "&:hover": { backgroundColor: p.dangerTint },
              "&:focus-visible": { outline: `2px solid ${focusRing}`, outlineOffset: "2px" },
            }}
          >
            <TrashSimpleIcon size={t.removeAll.iconSize} />
            <Box component="span">Remove all with errors</Box>
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        <Box
          component="button"
          type="button"
          onClick={startCsv}
          {...tourAttr("bulk.importCsv")}
          sx={{
            ...outlinedBtn,
            gap: t.action.gap,
            height: t.action.height,
            px: t.action.paddingX,
            borderRadius: t.action.radius,
            fontSize: t.action.fontSize,
          }}
        >
          <FileLinesIcon size={t.action.importIconSize} />
          Import CSV
        </Box>
        <Box
          component="button"
          type="button"
          onClick={addManual}
          {...tourAttr("bulk.addSigner")}
          sx={{
            ...solidBtn,
            gap: t.action.gap,
            height: t.action.height,
            px: t.action.paddingX,
            borderRadius: t.action.radius,
            fontSize: t.action.fontSize,
          }}
        >
          <PlusIcon size={t.action.addIconSize} />
          Add signer
        </Box>
      </Box>

      {/* imported banner */}
      {justImported && (
        <Box
          role="status"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: t.imported.gap,
            p: t.imported.padding,
            borderRadius: t.imported.radius,
            backgroundColor: t.imported.bg,
            border: t.imported.border,
          }}
        >
          <Box sx={{ display: "flex", color: t.imported.iconColor }}>
            <CheckIcon size={t.imported.iconSize} />
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: t.imported.fontSize,
              fontWeight: t.imported.fontWeight,
              color: t.imported.color,
            }}
          >
            {justImported}
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box
            component="button"
            type="button"
            onClick={() => setJustImported(null)}
            aria-label="Dismiss"
            sx={{
              border: "none",
              background: "none",
              padding: 0,
              width: t.imported.dismissSize,
              height: t.imported.dismissSize,
              borderRadius: t.imported.dismissRadius,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: t.imported.color,
              fontFamily: "inherit",
              "&:hover": { backgroundColor: t.imported.dismissHoverBg },
              "&:focus-visible": { outline: `2px solid ${focusRing}`, outlineOffset: "2px" },
            }}
          >
            <CloseIcon size={t.imported.dismissIconSize} />
          </Box>
        </Box>
      )}

      {/* empty states */}
      {totalCount === 0 && (
        <Box
          sx={{
            border: t.empty.border,
            borderRadius: t.empty.radius,
            p: t.empty.noRecipientsPadding,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: t.empty.noRecipientsGap,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: t.empty.noRecipientsFontSize,
              fontWeight: t.empty.noRecipientsFontWeight,
              color: t.empty.noRecipientsColor,
            }}
          >
            No signers yet
          </Box>
          <Box sx={{ display: "flex", gap: t.empty.btnGap }}>
            <Box
              component="button"
              type="button"
              onClick={addManual}
              sx={{
                ...solidBtn,
                height: t.empty.btnHeight,
                px: t.empty.btnPaddingX,
                borderRadius: t.empty.btnRadius,
                fontSize: t.empty.btnFontSize,
              }}
            >
              Add a signer
            </Box>
            <Box
              component="button"
              type="button"
              onClick={startCsv}
              sx={{
                ...outlinedBtn,
                height: t.empty.btnHeight,
                px: t.empty.btnPaddingX,
                borderRadius: t.empty.btnRadius,
                fontSize: t.empty.btnFontSize,
              }}
            >
              Import CSV
            </Box>
          </Box>
        </Box>
      )}

      {filteredEmpty && (
        <Box
          sx={{
            border: t.empty.border,
            borderRadius: t.empty.radius,
            p: t.empty.filteredPadding,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: t.empty.filteredFontSize,
              fontWeight: t.empty.filteredFontWeight,
              color: t.empty.filteredColor,
            }}
          >
            {filter === "errors"
              ? "No rows need fixing — everything is valid"
              : "No valid rows yet"}
          </Box>
        </Box>
      )}

      {/* rows */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: t.rows.gap }}>
        {rows.map((d) => (
          <Box key={d.signer.id} sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: t.rows.rowInnerGap,
                minHeight: t.rows.rowMinHeight,
                p: t.rows.rowPadding,
                border: `1px solid ${d.showError ? t.rows.errBorder : t.rows.okBorder}`,
                borderRadius: t.rows.rowRadius,
                backgroundColor: d.showError ? t.rows.errBg : t.rows.okBg,
                boxShadow: t.rows.rowShadow,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: t.rows.numSize,
                  height: t.rows.numSize,
                  borderRadius: "50%",
                  backgroundColor: t.rows.numBg,
                  color: t.rows.numColor,
                  fontSize: t.rows.numFontSize,
                  fontWeight: t.rows.numFontWeight,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {d.num}
              </Box>
              <Box
                component="input"
                value={d.signer.name || ""}
                placeholder="Enter Name"
                aria-label={`Signer ${d.num} name`}
                onMouseDown={markPointerFocus}
                onBlur={clearPointerFocus}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSignerField(d.signer.id, "name", e.target.value)
                }
                sx={{
                  ...inputBase,
                  flex: 1,
                  fontWeight: t.rows.nameFontWeight,
                  borderBottom: `1.5px solid ${t.rows.inputUnderline}`,
                }}
              />
              <Box
                component="input"
                value={d.signer.email || ""}
                placeholder="Enter Email"
                aria-label={`Signer ${d.num} email`}
                aria-invalid={d.showError || undefined}
                aria-describedby={
                  d.showError ? `bulk-signer-${d.signer.id}-error` : undefined
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSignerField(d.signer.id, "email", e.target.value)
                }
                onMouseDown={markPointerFocus}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  clearPointerFocus(e);
                  markTouched(d.signer.id);
                }}
                sx={{
                  ...inputBase,
                  flex: 1.2,
                  fontWeight: t.rows.emailFontWeight,
                  borderBottom: `1.5px solid ${
                    d.showError ? t.rows.inputUnderlineError : t.rows.inputUnderline
                  }`,
                }}
              />
              {d.fromCsv && (
                <Box
                  title={`Imported from ${d.source}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: t.rows.sourceGap,
                    height: t.rows.sourceHeight,
                    px: t.rows.sourcePaddingX,
                    borderRadius: t.rows.sourceRadius,
                    backgroundColor: t.rows.sourceBg,
                    color: t.rows.sourceColor,
                    fontSize: t.rows.sourceFontSize,
                    fontWeight: t.rows.sourceFontWeight,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    maxWidth: t.rows.sourceMaxWidth,
                  }}
                >
                  <FileIcon size={t.rows.sourceIconSize} />
                  <Box
                    component="span"
                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {d.source}
                  </Box>
                </Box>
              )}
              {d.showCheck && (
                <Box sx={{ display: "flex", color: t.rows.checkColor, flexShrink: 0 }}>
                  <CheckIcon size={t.rows.checkSize} />
                </Box>
              )}
              <Box
                component="button"
                type="button"
                onClick={() => removeRow(d.signer.id)}
                title="Remove"
                aria-label={`Remove signer ${d.num}`}
                sx={{
                  border: "none",
                  background: "none",
                  padding: 0,
                  width: t.rows.removeSize,
                  height: t.rows.removeSize,
                  borderRadius: t.rows.removeRadius,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: t.rows.removeColor,
                  flexShrink: 0,
                  fontFamily: "inherit",
                  "&:hover": {
                    backgroundColor: t.rows.removeHoverBg,
                    color: t.rows.removeHoverColor,
                  },
                  "&:focus-visible": { outline: `2px solid ${focusRing}`, outlineOffset: "2px" },
                }}
              >
                <TrashIcon size={t.rows.removeIconSize} />
              </Box>
            </Box>
            {d.showError && (
              <Box
                id={`bulk-signer-${d.signer.id}-error`}
                role="alert"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: t.rows.errorGap,
                  m: t.rows.errorMargin,
                  color: t.rows.errorColor,
                }}
              >
                <AlertCircleIcon size={t.rows.errorIconSize} />
                <Box
                  component="span"
                  sx={{
                    fontSize: t.rows.errorFontSize,
                    fontWeight: t.rows.errorFontWeight,
                  }}
                >
                  {d.err}
                </Box>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );

  const renderCsvUpload = () => (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: t.csvUpload.gap }}
    >
      <Box
        component="button"
        type="button"
        onClick={backToList}
        sx={{
          border: "none",
          background: "none",
          padding: 0,
          display: "flex",
          alignItems: "center",
          gap: t.csvUpload.backGap,
          color: t.csvUpload.backColor,
          fontSize: t.csvUpload.backFontSize,
          fontWeight: t.csvUpload.backFontWeight,
          cursor: "pointer",
          width: "fit-content",
          fontFamily: "inherit",
          "&:hover": { color: t.csvUpload.backHoverColor },
          "&:focus-visible": { outline: `2px solid ${focusRing}`, outlineOffset: "2px" },
        }}
      >
        <ArrowLeftIcon size={t.csvUpload.backIconSize} />
        <Box component="span">Back to signer list</Box>
      </Box>

      {/* Same surface as the Dashboard and the create-request step. It also
          brings real drag & drop, which the click-only button it replaced
          never had. */}
      <CustomDropzone
        uniqueId="bulk-send-csv"
        innerBody={
          <DropzoneBody
            title="Drop your CSV here"
            formatsText="One recipient per row · CSV up to 5 MB · max 500 recipients"
          />
        }
        sx={dropzoneSurfaceSx()}
        ariaLabel="Upload recipients CSV"
        onAdd={(files: File[]) => handleCsvFile(files[0])}
        maxFileSize={5 * 1024 * 1024}
        acceptedFiles={[".csv"]}
        showAlerts={false}
        multipleFileUpload={false}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: t.csvUpload.infoGap,
          p: t.csvUpload.infoPadding,
          borderRadius: t.csvUpload.infoRadius,
          backgroundColor: t.csvUpload.infoBg,
        }}
      >
        <Box sx={{ display: "flex", mt: "1px", color: t.csvUpload.infoIconColor }}>
          <InfoIcon size={t.csvUpload.infoIconSize} />
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: t.csvUpload.infoFontSize,
            fontWeight: t.csvUpload.infoFontWeight,
            color: t.csvUpload.infoColor,
            lineHeight: t.csvUpload.infoLineHeight,
            "& b": { color: t.csvUpload.infoBoldColor },
          }}
        >
          Your CSV should have a <b>Name</b> and an <b>Email</b> column. Column
          names don't have to match exactly — we'll help you map them after
          upload. Imported recipients are <b>added to</b> your existing list.
          Need a starting point? Use the <b>Download example CSV</b> button
          above.
        </Box>
      </Box>
    </Box>
  );

  const renderMapping = () => {
    const fields: { key: MapField; field: string }[] = [
      { key: "name", field: "Signer name" },
      { key: "email", field: "Signer email" },
    ];
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: t.mapping.gap }}>
        {/* file chip */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: t.mapping.fileChipGap,
              height: t.mapping.fileChipHeight,
              p: t.mapping.fileChipPadding,
              borderRadius: t.mapping.fileChipRadius,
              backgroundColor: t.mapping.fileChipBg,
              border: t.mapping.fileChipBorder,
            }}
          >
            <Box sx={{ display: "flex", color: t.mapping.fileChipIconColor }}>
              <FileIcon size={t.mapping.fileChipIconSize} />
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: t.mapping.fileChipNameFontSize,
                fontWeight: t.mapping.fileChipNameFontWeight,
                color: t.mapping.fileChipNameColor,
              }}
            >
              {csvFileName}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: t.mapping.fileChipRowsFontSize,
                fontWeight: t.mapping.fileChipRowsFontWeight,
                color: t.mapping.fileChipRowsColor,
              }}
            >
              {csvRaw.length} rows
            </Box>
            <Box
              component="button"
              type="button"
              onClick={cancelCsv}
              title="Remove and upload another file"
              aria-label="Remove file"
              sx={{
                border: "none",
                background: "none",
                padding: 0,
                width: t.mapping.fileChipRemoveSize,
                height: t.mapping.fileChipRemoveSize,
                borderRadius: t.mapping.fileChipRemoveRadius,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: t.mapping.fileChipRemoveColor,
                fontFamily: "inherit",
                "&:hover": { backgroundColor: t.mapping.fileChipRemoveHoverBg },
                "&:focus-visible": { outline: `2px solid ${focusRing}`, outlineOffset: "2px" },
              }}
            >
              <CloseIcon size={t.mapping.fileChipRemoveIconSize} />
            </Box>
          </Box>
        </Box>

        {/* AI banner */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: t.mapping.aiGap,
            p: t.mapping.aiPadding,
            borderRadius: t.mapping.aiRadius,
            backgroundColor: t.mapping.aiBg,
            border: t.mapping.aiBorder,
          }}
        >
          <Box sx={{ display: "flex", mt: "1px", color: t.mapping.aiIconColor }}>
            <SparkleIcon size={t.mapping.aiIconSize} />
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: t.mapping.aiFontSize,
              fontWeight: t.mapping.aiFontWeight,
              color: t.mapping.aiColor,
              lineHeight: t.mapping.aiLineHeight,
              "& b": { color: t.mapping.aiBoldColor },
            }}
          >
            <b>Columns mapped automatically by AI.</b> Your file's column names
            didn't match ours exactly, so we matched them for you — review below
            and change any mapping if we got it wrong.
          </Box>
        </Box>

        {/* mapping card */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: t.mapping.cardGap,
            border: t.mapping.cardBorder,
            borderRadius: t.mapping.cardRadius,
            p: t.mapping.cardPadding,
            backgroundColor: p.white,
            boxShadow: t.mapping.cardShadow,
          }}
        >
          {fields.map((f) => {
            const mapped = mapping[f.key];
            const open = openMenu === f.key;
            return (
              <Box
                key={f.key}
                sx={{ display: "flex", alignItems: "center", gap: t.mapping.mapRowGap }}
              >
                <Box
                  sx={{
                    width: t.mapping.fieldColWidth,
                    display: "flex",
                    alignItems: "center",
                    gap: t.mapping.fieldGap,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: t.mapping.fieldFontSize,
                      fontWeight: t.mapping.fieldFontWeight,
                      color: t.mapping.fieldColor,
                    }}
                  >
                    {f.field}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: t.mapping.requiredFontSize,
                      fontWeight: t.mapping.requiredFontWeight,
                      color: t.mapping.requiredColor,
                      backgroundColor: t.mapping.requiredBg,
                      borderRadius: t.mapping.requiredRadius,
                      p: t.mapping.requiredPadding,
                      letterSpacing: t.mapping.requiredLetterSpacing,
                    }}
                  >
                    REQUIRED
                  </Box>
                </Box>

                <Box sx={{ display: "flex", color: t.mapping.arrowIconColor, flexShrink: 0 }}>
                  <ArrowRightIcon size={t.mapping.arrowIconSize} />
                </Box>

                <Box data-map-root sx={{ position: "relative", flexShrink: 0 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setOpenMenu(open ? null : f.key)}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-label={`${f.field} column`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: t.mapping.selGap,
                      height: t.mapping.selHeight,
                      minWidth: t.mapping.selMinWidth,
                      px: t.mapping.selPaddingX,
                      border: `1px solid ${
                        mapped ? t.mapping.selBorderMapped : t.mapping.selBorderUnmapped
                      }`,
                      borderRadius: t.mapping.selRadius,
                      fontFamily: "inherit",
                      fontSize: t.mapping.selFontSize,
                      fontWeight: t.mapping.selFontWeight,
                      color: mapped ? t.mapping.selColorMapped : t.mapping.selColorUnmapped,
                      cursor: "pointer",
                      backgroundColor: p.white,
                      boxSizing: "border-box",
                      "&:hover": { borderColor: t.mapping.selHoverBorder },
                      "&:focus-visible": { outline: `2px solid ${focusRing}`, outlineOffset: "2px" },
                    }}
                  >
                    <Box component="span" sx={{ flex: 1 }}>
                      {mapped || "Select a column…"}
                    </Box>
                    <Box sx={{ display: "flex", color: t.mapping.selCaretColor }}>
                      <ChevronDownIcon size={t.mapping.selCaretSize} />
                    </Box>
                  </Box>
                  {open && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: t.mapping.menuTop,
                        left: 0,
                        width: t.mapping.menuWidth,
                        backgroundColor: p.white,
                        border: t.mapping.menuBorder,
                        borderRadius: t.mapping.menuRadius,
                        boxShadow: t.mapping.menuShadow,
                        p: t.mapping.menuPadding,
                        zIndex: 40,
                      }}
                    >
                      {cols.map((col) => (
                        <Box
                          key={col}
                          component="button"
                          type="button"
                          onClick={() => mapPick(f.key, col)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: t.mapping.optionPadding,
                            borderRadius: t.mapping.optionRadius,
                            fontFamily: "inherit",
                            fontSize: t.mapping.optionFontSize,
                            fontWeight: t.mapping.optionFontWeight,
                            color: t.mapping.optionColor,
                            cursor: "pointer",
                            width: "100%",
                            textAlign: "left",
                            border: "none",
                            background: "transparent",
                            "&:hover": { backgroundColor: t.mapping.optionHoverBg },
                            "&:focus-visible": { outline: `2px solid ${focusRing}`, outlineOffset: "2px" },
                          }}
                        >
                          <Box component="span">{col}</Box>
                          {col === mapped && (
                            <Box sx={{ display: "flex", color: t.mapping.optionCheckColor }}>
                              <CheckIcon size={t.mapping.optionCheckSize} />
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                {!!mapped && aiMapped[f.key] && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: t.mapping.aiTagGap,
                      height: t.mapping.aiTagHeight,
                      px: t.mapping.aiTagPaddingX,
                      borderRadius: t.mapping.aiTagRadius,
                      backgroundColor: t.mapping.aiTagBg,
                      color: t.mapping.aiTagColor,
                      fontSize: t.mapping.aiTagFontSize,
                      fontWeight: t.mapping.aiTagFontWeight,
                      letterSpacing: t.mapping.aiTagLetterSpacing,
                      flexShrink: 0,
                    }}
                  >
                    <SparkleSmallIcon size={t.mapping.aiTagIconSize} />
                    AI MAPPED
                  </Box>
                )}

                <Box
                  component="span"
                  sx={{
                    fontSize: t.mapping.sampleFontSize,
                    fontWeight: t.mapping.sampleFontWeight,
                    color: t.mapping.sampleColor,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  e.g. {mapped && csvRaw.length ? csvRaw[0][mapped] : "—"}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* preview */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            border: t.mapping.previewBorder,
            borderRadius: t.mapping.previewRadius,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              p: t.mapping.previewHeadPadding,
              backgroundColor: t.mapping.previewHeadBg,
              borderBottom: t.mapping.previewHeadBorder,
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: t.mapping.previewTitleFontSize,
                fontWeight: t.mapping.previewTitleFontWeight,
                color: t.mapping.previewTitleColor,
                letterSpacing: t.mapping.previewTitleLetterSpacing,
              }}
            >
              FILE PREVIEW
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: t.mapping.previewMetaFontSize,
                fontWeight: t.mapping.previewMetaFontWeight,
                color: t.mapping.previewMetaColor,
              }}
            >
              first {Math.min(3, csvRaw.length)} of {csvRaw.length} rows
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              // `minmax(0,…)`: these cells hold arbitrary CSV headers and
              // values, and a bare `1fr` floors the track at min-content — one
              // long value would widen the preview past the page instead of
              // truncating.
              gridTemplateColumns: "repeat(3,minmax(0,1fr))",
              borderBottom: `1px solid ${p.borderLight}`,
              backgroundColor: p.white,
            }}
          >
            {cols.slice(0, 3).map((c) => (
              <Box
                key={c}
                sx={{
                  p: "9px 16px",
                  fontSize: "11.5px",
                  fontWeight: 800,
                  color: p.muted,
                  letterSpacing: ".03em",
                  borderRight: `1px solid ${p.divider}`,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {c}
              </Box>
            ))}
          </Box>

          {csvRaw.slice(0, 3).map((r, ri) => (
            <Box
              key={ri}
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3,minmax(0,1fr))",
                borderBottom: `1px solid ${p.divider}`,
                backgroundColor: p.white,
              }}
            >
              {cols.slice(0, 3).map((c, ci) => (
                <Box
                  key={c}
                  sx={{
                    p: "9px 16px",
                    fontSize: "12.5px",
                    fontWeight: ci === 0 ? 600 : 500,
                    color: ci === 2 ? p.body : p.ink,
                    borderRight: `1px solid ${p.divider}`,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {r[c] ?? ""}
                </Box>
              ))}
            </Box>
          ))}
        </Box>

        {/* footer actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: t.mapping.footerGap }}>
          <Box
            component="button"
            type="button"
            onClick={cancelCsv}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: t.mapping.cancelHeight,
              px: t.mapping.cancelPaddingX,
              borderRadius: t.mapping.cancelRadius,
              border: t.mapping.cancelBorder,
              backgroundColor: p.white,
              color: t.mapping.cancelColor,
              fontFamily: "inherit",
              fontSize: t.mapping.cancelFontSize,
              fontWeight: t.mapping.cancelFontWeight,
              cursor: "pointer",
              "&:hover": { borderColor: t.mapping.cancelHoverBorder },
              "&:focus-visible": {
                outline: `2px solid ${focusRing}`,
                outlineOffset: "2px",
              },
            }}
          >
            Cancel
          </Box>
          <Box
            component="button"
            type="button"
            onClick={confirmMapping}
            aria-disabled={!mapReady}
            title={mapReady ? "" : "Map both the name and email columns first"}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: t.mapping.confirmHeight,
              px: t.mapping.confirmPaddingX,
              borderRadius: t.mapping.confirmRadius,
              border: "none",
              backgroundColor: mapReady
                ? t.mapping.confirmReadyBg
                : t.mapping.confirmDisabledBg,
              color: t.mapping.confirmColor,
              fontFamily: "inherit",
              fontSize: t.mapping.confirmFontSize,
              fontWeight: t.mapping.confirmFontWeight,
              cursor: mapReady ? "pointer" : "not-allowed",
              "&:focus-visible": {
                outline: `2px solid ${focusRing}`,
                outlineOffset: "2px",
              },
            }}
          >
            Add {csvRaw.length} recipients to list
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        maxWidth: t.page.maxWidth,
        mx: "auto",
        p: t.page.padding,
        boxSizing: "border-box",
      }}
    >
      {/* heading + download */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "20px",
          mb: t.head.marginBottom,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: t.head.gap }}>
          <Box
            component="h1"
            sx={{
              m: 0,
              fontSize: t.head.titleFontSize,
              fontWeight: t.head.titleFontWeight,
              letterSpacing: t.head.titleLetterSpacing,
              color: t.head.titleColor,
            }}
          >
            Add signers
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: t.head.subFontSize,
              fontWeight: t.head.subFontWeight,
              color: t.head.subColor,
            }}
          >
            Each recipient gets their own copy of the document to sign. Add
            signers one by one, import a CSV, or both.
          </Box>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={downloadSample}
          sx={{
            ...outlinedBtn,
            gap: t.head.downloadGap,
            height: t.head.downloadHeight,
            px: t.head.downloadPaddingX,
            borderRadius: t.head.downloadRadius,
            fontSize: t.head.downloadFontSize,
            flexShrink: 0,
          }}
        >
          <DownloadIcon size={t.head.downloadIconSize} />
          Download example CSV
        </Box>
      </Box>

      {view === "list" && renderList()}
      {view === "csvUpload" && renderCsvUpload()}
      {view === "csvMapping" && renderMapping()}
    </Box>
  );
};

export default BulkSendParticipants;
